import { faBars, faBell, faBookmark, faChevronRight, faClose, faFilm, faGamepad, faMoon, faPowerOff, faSearch, faSun, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import Link from "next/link";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import Logo from "../../assets/images/gameson-logo.webp";
import ButtonCapsule from "../ui/ButtonCapsule";
import { useSession, signIn, signOut } from "next-auth/react";
import { useRouter } from "next/router";
import { useTheme } from "../../contexts/ThemeContext";
import { useNotifications } from "../../contexts/NotificationsContext";
import NotificationDropdown from "./NotificationDropdown";

import { DEFAULT_AVATAR } from "../../utils/image";
import { searchContents } from "../../utils/search";
import type { SearchItem } from "../../utils/search";

// Only opacity steps from Tailwind's default scale (5, 10, 20, 25, 30, 40, 50,
// 60, 70, 75, 80, 90, 95) are generated: the previous `/15`, `/85`, `/6`, `/12`
// variants compiled to nothing, so the overlay silently inherited the page's
// colours — dark text on the dark card in light mode. Every colour below is a
// theme token or a `dark:` pair, so the overlay follows the site theme.
const CATEGORY_COLORS: Record<string, string> = {
  News: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
  Review: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  Feature: "bg-purple-500/10 text-purple-700 dark:text-purple-300",
  Guide: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};

// The card colours: white on the light theme, the original navy on dark.
const CARD = "bg-theme-card border border-theme-border dark:border-white/10";
const SUBTLE_BG = "bg-black/5 dark:bg-white/10";

const SearchOverlay = ({
  query,
  results,
  isLoading,
  onSelect,
  onClose,
  onQueryChange,
  onSubmit,
  inputRef,
}: {
  query: string;
  results: SearchItem[];
  isLoading: boolean;
  onSelect: () => void;
  onClose: () => void;
  onQueryChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) => {
  const showResults = query.trim().length > 0;
  return (
    <div
      className="fixed inset-0 z-[9999] bg-stage/70 dark:bg-black/75 flex flex-col items-center pt-16 md:pt-24 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl text-theme-text"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input card */}
        <form
          onSubmit={onSubmit}
          className={`flex items-center gap-3 rounded-[4px] px-5 h-14 ${CARD} shadow-float`}
        >
          {isLoading ? (
            <span className="h-5 w-5 block rounded-full border-2 border-theme-border !border-t-signal animate-spin shrink-0" />
          ) : (
            <FontAwesomeIcon icon={faSearch} className="text-theme-text-secondary text-base shrink-0" />
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search for news, reviews, guides..."
            aria-label="Search"
            className="flex-1 bg-transparent text-theme-text text-lg outline-none placeholder:text-theme-text-secondary"
          />
          {query && (
            <button
              type="button"
              onClick={() => onQueryChange("")}
              className={`flex h-6 w-6 items-center justify-center rounded-full ${SUBTLE_BG} hover:bg-black/10 dark:hover:bg-white/20 text-theme-text-secondary hover:text-theme-text transition-colors shrink-0`}
              aria-label="Clear search"
            >
              <FontAwesomeIcon icon={faClose} className="text-xs" />
            </button>
          )}
          <kbd className={`hidden sm:flex items-center gap-0.5 px-2 py-1 rounded-lg ${SUBTLE_BG} border border-theme-border dark:border-white/10 text-[10px] text-theme-text-secondary font-mono shrink-0`}>
            ESC
          </kbd>
        </form>

        {/* Results card */}
        {showResults && (
          <div className={`mt-2 rounded-[4px] ${CARD} shadow-float overflow-hidden`}>
            {isLoading ? (
              <div className="flex items-center gap-3 px-5 py-5 text-sm text-theme-text-secondary">
                <span className="h-4 w-4 block rounded-full border-2 border-theme-border !border-t-signal animate-spin" />
                Searching for &ldquo;{query}&rdquo;...
              </div>
            ) : results.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
                <FontAwesomeIcon icon={faSearch} className="text-2xl text-theme-text-secondary opacity-60" />
                <p className="text-sm text-theme-text">No results for &ldquo;{query}&rdquo;</p>
                <p className="text-xs text-theme-text-secondary">Try different keywords</p>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-theme-border dark:divide-white/5">
                  {results.slice(0, 7).map((item) => (
                    <li key={item._id}>
                      <Link
                        href={`/article/${item._id}`}
                        prefetch={false}
                        onClick={onSelect}
                        className="flex items-center gap-4 px-4 py-3 hover:bg-black/5 dark:hover:bg-white/5 transition-colors group"
                      >
                        <div className={`relative w-20 h-[45px] rounded-[3px] overflow-hidden shrink-0 ${SUBTLE_BG}`}>
                          {item.thumbnail ? (
                            <Image src={item.thumbnail} fill sizes="64px" alt="" className="object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FontAwesomeIcon icon={faSearch} className="text-theme-text-secondary text-xs" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-theme-text group-hover:text-brand-main dark:group-hover:text-white truncate leading-snug transition-colors">
                            {item.title}
                          </p>
                          {item.category && (
                            <span className={`inline-block mt-1 px-2 py-0.5 font-display text-xs font-semibold ${CATEGORY_COLORS[item.category] ?? `${SUBTLE_BG} text-theme-text-secondary`}`}>
                              {item.category}
                            </span>
                          )}
                        </div>
                        <FontAwesomeIcon icon={faChevronRight} className="text-[10px] text-theme-text-secondary opacity-60 group-hover:opacity-100 transition-opacity shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-theme-border dark:border-white/10">
                  <Link
                    href={`/search/${encodeURIComponent(query)}`}
                    onClick={onSelect}
                    className="flex items-center justify-between px-5 py-3 text-sm font-medium text-brand-main dark:text-brand-main-800 hover:bg-brand-main/10 transition-colors group"
                  >
                    <span>See all results for &ldquo;{query}&rdquo;</span>
                    <FontAwesomeIcon icon={faChevronRight} className="text-[10px] group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Navbar = () => {
  const { data: session } = useSession();
  const { theme, toggleTheme } = useTheme();
  const { unreadCount } = useNotifications();
  const [hasHydrated, setHasHydrated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isPopup, setIsPopup] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [pictureProfile, setPictureProfile] = useState("");
  const [displayName, setDisplayName] = useState("");
  const sessionUserId = session?.user?._id;
  const sessionPicture = session?.user?.pictureProfile;
  const sessionDisplayName = session?.user?.displayName;
  const switchShowStatus = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedPicture = localStorage.getItem("myData");
    if (storedPicture && storedPicture !== "undefined" && storedPicture !== "null") {
      setPictureProfile(storedPicture);
    }
    const storedName = localStorage.getItem("myDisplayName");
    if (storedName && storedName !== "undefined" && storedName !== "null") {
      setDisplayName(storedName);
    }
  }, []);

  // The session carries pictureProfile/displayName (filled in the jwt callback
  // at sign-in and on useSession().update()); it wins over the localStorage
  // copy, which is only the pre-session seed for the next page load.
  useEffect(() => {
    if (sessionPicture === undefined) return;
    setPictureProfile(sessionPicture);
    if (sessionPicture) {
      localStorage.setItem("myData", sessionPicture);
    } else {
      localStorage.removeItem("myData");
    }
    if (sessionDisplayName) {
      setDisplayName(sessionDisplayName);
      localStorage.setItem("myDisplayName", sessionDisplayName);
    }
  }, [sessionPicture, sessionDisplayName]);

  useEffect(() => {
    if (!session || pictureProfile !== "" || typeof window === "undefined") return;
    // "" is both "no picture" and "not loaded yet", which used to send every
    // picture-less user to /auth/me on every mount. The per-user sentinel
    // (cleared in handleSignOut) remembers the answer across mounts.
    if (sessionUserId && localStorage.getItem("myNoPicture") === sessionUserId) return;
    if (sessionPicture === "") {
      if (sessionUserId) localStorage.setItem("myNoPicture", sessionUserId);
      return;
    }
    if (sessionPicture) return; // the effect above is applying it
    // Session minted before the profile fields existed (or the sign-in-time
    // fetch failed): one client fetch, and the single place a dead backend
    // token signs the user out.
    fetch(`${process.env.BACKEND_BASE_URL}/auth/me`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${session.user.accessToken}`,
      },
    })
      .then(async (res) => {
        if (res.status === 401) {
          signOut();
          return;
        }
        const data = await res.json();
        if (data?.success !== true) return;
        const pic = data.data.pictureProfile || "";
        const name = data.data.displayName || "";
        setPictureProfile(pic);
        setDisplayName(name);
        if (pic) {
          localStorage.setItem("myData", pic);
        } else {
          localStorage.removeItem("myData");
          if (sessionUserId) localStorage.setItem("myNoPicture", sessionUserId);
        }
        if (name) {
          localStorage.setItem("myDisplayName", name);
        } else {
          localStorage.removeItem("myDisplayName");
        }
      })
      .catch(() => {});
  }, [session, sessionUserId, sessionPicture, pictureProfile]);

  const handleSignOut = async () => {
    // Clear the cached avatar/name/sentinel before the redirect so the next
    // account on this browser never inherits them.
    localStorage.removeItem("myData");
    localStorage.removeItem("myDisplayName");
    localStorage.removeItem("myNoPicture");
    // Sign out the user and delete their session information from the server
    await signOut({ callbackUrl: `/` });
  };

  const router = useRouter();
  const isArticle = router.pathname.includes("article");
  const isAuthenticated = hasHydrated && !!session;
  // The home page's own crawl carries the sign-up action (HeroBanner).
  // Not on auth pages either: there the card itself is the sign-in action.
  const showJoinBanner = !isArticle && !isAuthenticated && router.pathname !== "/" && !router.pathname.startsWith("/auth");

  const profileRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isPopup) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isPopup]);

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState<SearchItem[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounced search. Results are cached per query in utils/search, so the
  // /search page reuses them on submit instead of fetching the same query.
  useEffect(() => {
    const q = searchText.trim();
    if (!q) {
      setSearchResults([]);
      setIsSearchLoading(false);
      return;
    }
    setIsSearchLoading(true);
    let stale = false;
    const timer = setTimeout(() => {
      searchContents(q)
        .then((items) => {
          if (!stale) setSearchResults(items);
        })
        .catch(() => {})
        .finally(() => {
          if (!stale) setIsSearchLoading(false);
        });
    }, 400);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [searchText]);

  // Focus input when overlay opens
  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchText("");
      setSearchResults([]);
    }
  }, [isSearchOpen]);

  // Keyboard shortcut: / to open, ESC to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setIsSearchOpen(false); return; }
      if (e.key === "/" && !isSearchOpen && (e.target as HTMLElement).tagName !== "INPUT" && (e.target as HTMLElement).tagName !== "TEXTAREA") {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isSearchOpen]);

  const clearSearch = useCallback(() => {
    setSearchText("");
    setSearchResults([]);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  const searchHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchText.trim()) return;
    setIsSearchOpen(false);
    if (!router.pathname.includes("search")) {
      router.replace(`/search/${searchText}`);
    } else {
      router.push(`${searchText}`);
    }
  };

  return (
    <Fragment>
      <div
        className={
          showJoinBanner ? "relative h-12 lg:block hidden" : "hidden"
        }
      ></div>
      <div className="lg:flex bg-stage-deep w-full h-20 hidden fixed top-0 left-0 z-50">
        <div className="text-white w-full h-full">
          {/* 1fr | auto | 1fr keeps the links centred at their natural width;
              equal thirds left them ~320px at 1024 and "ปฏิทินเกม" wrapped. */}
          <div className="h-full grid grid-cols-[1fr_auto_1fr] gap-4 items-center px-8">
            {/* Left — Logo */}
            <div className="flex items-center">
              <Link
                href="/"
                prefetch={false}
                rel="noopener noreferrer"
                className="shrink-0"
                aria-label="Games On home"
              >
                <Image
                  src={Logo}
                  width={160}
                  height={24}
                  alt="Games On"
                  className="w-40 h-6 object-contain cursor-pointer"
                />
              </Link>
            </div>

            {/* Center — Nav links */}
            <nav aria-label="Sections" className="flex items-center justify-center gap-1">
              {[
                { href: "/news", label: "ข่าว" },
                { href: "/review", label: "รีวิว" },
                { href: "/feature", label: "บทความ" },
                { href: "/guide", label: "ไกด์" },
                { href: "/calendar", label: "ปฏิทินเกม" },
              ].map(({ href, label }) => {
                const isActive = router.pathname === href || router.pathname.startsWith(href + "/");
                return (
                  <Link
                    key={href}
                    href={href}
                    prefetch={false}
                    aria-current={isActive ? "page" : undefined}
                    className={`relative inline-flex h-10 items-center px-4 font-display text-[17px] font-semibold whitespace-nowrap transition-colors duration-150 ${
                      isActive
                        ? "bg-signal text-white"
                        : "text-white/65 hover:text-white hover:bg-white/8"
                    }`}
                  >
                    {label}
                  </Link>
                );
              })}
            </nav>

            {/* Right — Actions */}
            <div className="flex items-center justify-end gap-4 whitespace-nowrap">
              <button
                onClick={() => setIsSearchOpen(true)}
                className="flex h-10 w-56 xl:w-64 items-center gap-2.5 rounded-[3px] bg-white/8 px-3.5 text-sm text-white/60 transition-colors duration-150 hover:bg-white/12 hover:text-white group"
                aria-label="Open search"
                aria-keyshortcuts="/"
              >
                <FontAwesomeIcon icon={faSearch} className="text-sm" />
                <span className="flex-1 text-left">ค้นหาข่าว รีวิว ไกด์</span>
                <kbd className="flex h-5 min-w-[20px] items-center justify-center rounded-[2px] bg-white/8 px-1 font-display text-xs text-white/50 group-hover:text-white/70 transition-colors">
                  /
                </kbd>
              </button>
              <div className={isAuthenticated ? "flex items-center gap-3" : "relative"}>
                {isAuthenticated ? (
                  <Fragment>
                    {/* Notification Bell */}
                    <div className="relative">
                      <button
                        onClick={() => { setIsNotifOpen((prev) => !prev); setIsPopup(false); }}
                        className="relative flex h-10 w-10 items-center justify-center hover:bg-white/10 transition-colors"
                        aria-label="Notifications"
                        aria-haspopup="dialog"
                        aria-expanded={isNotifOpen}
                      >
                        <FontAwesomeIcon icon={faBell} className="text-white text-lg" />
                        {unreadCount > 0 && (
                          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] bg-score text-stage font-display text-xs font-semibold flex items-center justify-center px-1 leading-none tabular-nums">
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                      </button>
                      {isNotifOpen && (
                        <NotificationDropdown onClose={() => setIsNotifOpen(false)} />
                      )}
                    </div>

                    {/* Profile Picture + Dropdown */}
                    <div ref={profileRef} className="relative">
                      <button
                        onClick={() => { setIsPopup((prev) => !prev); setIsNotifOpen(false); }}
                        className="relative block w-9 h-9 rounded-full ring-2 ring-white/15 hover:ring-signal transition-all"
                        aria-label="Open profile menu"
                      >
                        <Image
                          src={pictureProfile || DEFAULT_AVATAR}
                          fill
                          sizes="32px"
                          alt="Profile"
                          className="rounded-full object-cover"
                        />
                      </button>

                      {isPopup && (
                        <div className="absolute top-12 right-0 z-50 w-60 animate-slide-up">
                          <div className="rounded-[4px] overflow-hidden shadow-float bg-stage-panel">

                            {/* User header */}
                            <div className="flex items-center gap-3 px-4 py-3.5 bg-white/5">
                              <div className="relative w-9 h-9 rounded-full overflow-hidden ring-2 ring-brand-main/50 shrink-0">
                                <Image
                                  src={pictureProfile || DEFAULT_AVATAR}
                                  fill sizes="36px" alt="Profile"
                                  className="object-cover"
                                />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-white truncate leading-tight">
                                  {displayName || session?.user?.name || ""}
                                </p>
                                <p className="text-xs text-white/60 truncate leading-tight mt-1">
                                  {session?.user?.email || ""}
                                </p>
                              </div>
                            </div>

                            {/* Menu items */}
                            <div className="p-1.5 flex flex-col gap-0.5">
                              {[
                                { href: "/profile", icon: faUser, label: "Manage Profile" },
                                { href: "/profile?tab=saved", icon: faBookmark, label: "Read List" },
                                { href: "/profile?tab=watchlist", icon: faFilm, label: "Watch List" },
                              ].map((item) => (
                                <Link
                                  key={item.label}
                                  href={item.href}
                                  onClick={() => setIsPopup(false)}
                                  className="group flex items-center gap-3 px-3 py-2.5 hover:bg-white/8 transition-colors"
                                >
                                  <FontAwesomeIcon icon={item.icon} className="w-4 text-sm text-white/50 group-hover:text-white transition-colors" />
                                  <span className="flex-1 text-sm font-medium text-white/80 group-hover:text-white transition-colors">
                                    {item.label}
                                  </span>
                                  <FontAwesomeIcon icon={faChevronRight} className="text-[10px] text-white/30 group-hover:text-white/60 transition-colors" />
                                </Link>
                              ))}
                            </div>

                            {/* Sign out */}
                            <div className="px-1.5 pb-1.5">
                              <div className="border-t border-white/8 pt-1.5">
                                <button
                                  onClick={handleSignOut}
                                  className="group flex items-center gap-3 w-full px-3 py-2.5 hover:bg-red-500/12 transition-colors"
                                >
                                  <FontAwesomeIcon icon={faPowerOff} className="w-4 text-sm text-white/50 group-hover:text-red-300 transition-colors" />
                                  <span className="text-sm font-medium text-white/80 group-hover:text-red-300 transition-colors">
                                    Sign Out
                                  </span>
                                </button>
                              </div>
                            </div>

                          </div>
                        </div>
                      )}
                    </div>
                  </Fragment>
                ) : (
                  <ButtonCapsule onClick={() => signIn()}>
                    Sign In
                  </ButtonCapsule>
                )}
              </div>
              <button
                onClick={toggleTheme}
                className="flex h-10 w-10 items-center justify-center text-white/75 hover:text-white hover:bg-white/10 transition-colors"
                aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                <FontAwesomeIcon
                  icon={theme === "dark" ? faSun : faMoon}
                  className="text-base"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={
          showJoinBanner
            ? "lg:flex bg-signal absolute mt-20 text-white w-full h-12 hidden top-0 left-0 z-40 gap-4 items-center justify-center [--color-focus:#FFFFFF]"
            : "hidden"
        }
      >
          <p className="font-medium">{`Join the Game's on Community!`}</p>
          <button
            type="button"
            onClick={() => signIn()}
            className="inline-flex h-8 items-center bg-white px-3 font-display text-sm font-semibold text-signal transition-colors hover:bg-brand-main-100"
          >
            Sign up
          </button>
      </div>
      {/* Mobile top bar */}
      <div className={showJoinBanner ? "h-4 lg:hidden block" : "hidden"}></div>
      <div className="lg:hidden bg-stage-deep w-full fixed top-0 left-0 z-50 text-white">
        <div className="h-16 flex items-center justify-between md:px-8 px-4">
          <Link href="/" prefetch={false} rel="noopener noreferrer" aria-label="Games On home">
            <Image
              src={Logo}
              width={160}
              height={24}
              alt="Games On"
              className="w-36 h-6 object-contain"
            />
          </Link>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsSearchOpen(true)}
              className="flex h-11 w-11 items-center justify-center hover:bg-white/10 transition-colors"
              aria-label="Search"
            >
              <FontAwesomeIcon icon={faSearch} className="text-base" />
            </button>
            <button
              onClick={toggleTheme}
              className="flex h-11 w-11 items-center justify-center hover:bg-white/10 transition-colors"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              <FontAwesomeIcon icon={theme === "dark" ? faSun : faMoon} className="text-base" />
            </button>
            <button
              onClick={switchShowStatus}
              aria-expanded={isOpen}
              className="flex h-11 w-11 items-center justify-center hover:bg-white/10 transition-colors"
              aria-label="Open menu"
            >
              <FontAwesomeIcon icon={faBars} className="text-xl" />
            </button>
          </div>
        </div>
      </div>
      <div
        className={
          showJoinBanner
            ? "lg:hidden absolute bg-signal mt-16 w-full text-white h-8 flex top-0 left-0 z-30 gap-2 items-center justify-center [--color-focus:#FFFFFF]"
            : "hidden"
        }
      >
        <p className="text-sm font-medium">{`Join the Game's on Community!`}</p>
        <button type="button" onClick={() => signIn()} className="font-display text-sm font-semibold underline underline-offset-2 bg-transparent border-0 p-0 m-0 text-inherit">Sign up</button>
      </div>

      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/60 z-[9998] lg:hidden transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setIsOpen(false)}
      />

      {/* Slide-in drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-[300px] max-w-[85vw] bg-stage-deep z-[9999] lg:hidden flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${isOpen ? "translate-x-0 shadow-float" : "translate-x-full"}`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 h-16 border-b border-white/10 shrink-0">
          <Link href="/" prefetch={false} onClick={() => setIsOpen(false)} rel="noopener noreferrer">
            <Image src={Logo} width={140} height={21} alt="Games On" className="w-36 h-6 object-contain" />
          </Link>
          <button
            onClick={() => setIsOpen(false)}
            className="flex h-11 w-11 items-center justify-center hover:bg-white/10 transition-colors text-white"
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faClose} className="text-xl" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-4 border-b border-white/10 shrink-0">
          <button
            onClick={() => { setIsSearchOpen(true); setIsOpen(false); }}
            className="flex items-center gap-3 w-full h-11 px-4 rounded-[3px] bg-white/8 text-white/60 hover:bg-white/12 transition-colors text-sm"
          >
            <FontAwesomeIcon icon={faSearch} className="text-sm" />
            <span>ค้นหาข่าว รีวิว ไกด์</span>
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto py-2">
          {[
            { href: "/news", label: "ข่าว" },
            { href: "/review", label: "รีวิว" },
            { href: "/feature", label: "บทความ" },
            { href: "/guide", label: "ไกด์" },
            { href: "/calendar", label: "ปฏิทินเกม" },
          ].map(({ href, label }) => {
            const isActive = router.pathname === href || router.pathname.startsWith(href + "/");
            return (
              <Link
                key={href}
                href={href}
                prefetch={false}
                onClick={() => setIsOpen(false)}
                aria-current={isActive ? "page" : undefined}
                className={`flex items-center px-5 py-3.5 font-display text-lg font-semibold transition-colors ${
                  isActive
                    ? "bg-signal text-white"
                    : "text-white/80 hover:text-white hover:bg-white/8"
                }`}
              >
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Auth + theme section */}
        <div className="border-t border-white/10 p-5 flex flex-col gap-3 shrink-0">
          {isAuthenticated ? (
            <>
              <Link
                href="/profile"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 py-1 text-sm text-white/80 hover:text-white transition-colors"
              >
                <div className="relative w-7 h-7 rounded-full overflow-hidden ring-2 ring-brand-main/50 shrink-0">
                  <Image
                    src={pictureProfile || DEFAULT_AVATAR}
                    fill
                    sizes="28px"
                    alt="Profile"
                    className="object-cover"
                  />
                </div>
                <span className="font-medium">Manage Profile</span>
              </Link>
              <Link
                href="/profile?tab=saved"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 py-1 text-sm text-white/80 hover:text-white transition-colors"
              >
                <div className="flex h-7 w-7 items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faBookmark} className="text-sm text-brand-main/80" />
                </div>
                <span className="font-medium">Read List</span>
              </Link>
              <Link
                href="/profile?tab=watchlist"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 py-1 text-sm text-white/80 hover:text-white transition-colors"
              >
                <div className="flex h-7 w-7 items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faGamepad} className="text-sm text-brand-main/80" />
                </div>
                <span className="font-medium">Watchlist</span>
              </Link>
              <Link
                href="/profile?tab=notifications"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 py-1 text-sm text-white/80 hover:text-white transition-colors"
              >
                <div className="relative flex h-7 w-7 items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faBell} className="text-sm text-brand-main/80" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-score text-stage font-display text-[11px] font-semibold flex items-center justify-center px-0.5 tabular-nums">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </div>
                <span className="font-medium">
                  Notifications {unreadCount > 0 && `(${unreadCount})`}
                </span>
              </Link>
              <button
                onClick={() => { handleSignOut(); setIsOpen(false); }}
                className="text-left text-sm text-red-400 hover:text-red-300 transition-colors py-1"
              >
                Sign Out
              </button>
            </>
          ) : (
            <button
              onClick={() => { signIn(); setIsOpen(false); }}
              className="btn-signal w-full h-11"
            >
              Sign In
            </button>
          )}
          <button
            onClick={toggleTheme}
            className="flex items-center gap-3 text-sm text-white/50 hover:text-white/80 transition-colors py-1"
          >
            <FontAwesomeIcon icon={theme === "dark" ? faSun : faMoon} className="text-base" />
            <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
          </button>
        </div>
      </div>
      {/* Search overlay */}
      {isSearchOpen && (
        <SearchOverlay
          query={searchText}
          results={searchResults}
          isLoading={isSearchLoading}
          onSelect={() => { closeSearch(); setIsOpen(false); }}
          onClose={closeSearch}
          onQueryChange={setSearchText}
          onSubmit={searchHandler}
          inputRef={searchInputRef}
        />
      )}
    </Fragment>
  );
};

export default Navbar;
